import React from 'react';
import { Dialog, DialogActions, DialogContent, Button, Box, TextField, Typography } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import * as XLSX from 'xlsx';

const RfqGridExpansionModal = ({
  open,
  onClose,
  columnDefs,
  defaultColDef,
  rowData,
  setRowData,
  rowsToAdd,
  setRowsToAdd,
  handleAddRow,
  selectedProjectSubsidiaryPairs,
  projects,
  accounts,
  uoms,
  onImportExcel,
  isBiddingEnabled,
  setDeletedRowIds,
}) => {
  const handleExportTemplate = () => {
    const warningNote = [
      [
        'NOTE: Do NOT add rows inbetween here and the table below. Only valid Projects + Subsidiary pairs, Material Categories, and UOMs from the helper sheets will import correctly.',
      ],
      [],
    ];

    const coreSheetData = [['Project', 'Subsidiary', 'Material Category', 'Quantity', 'UOM', 'Description']];

    const projectSheet = [
      ['Project Name', 'Subsidiary Name'],
      ...selectedProjectSubsidiaryPairs
        .map((pair) => {
          const proj = projects.find((p) => p.projectId === pair.projectId);
          return [proj?.name || 'UNKNOWN PROJECT', pair.subsidiaryName || 'UNKNOWN SUBSIDIARY'];
        })
        .sort((a, b) => {
          const projectCompare = a[0].localeCompare(b[0]);
          if (projectCompare !== 0) return projectCompare;
          return a[1].localeCompare(b[1]);
        }),
    ];
    const materialCategorySheet = [['Material Category'], ...accounts.map((acct) => [acct.fullname])];

    const uomSheet = [['UOM'], ...uoms.map((u) => [u.name])];

    const wb = XLSX.utils.book_new();
    const lineItemSheet = XLSX.utils.aoa_to_sheet([...warningNote, ...coreSheetData]);

    lineItemSheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];
    lineItemSheet['!cols'] = [{ wch: 50 }, { wch: 50 }, { wch: 50 }, { wch: 20 }, { wch: 20 }, { wch: 20 }];

    const projectsSheet = XLSX.utils.aoa_to_sheet(projectSheet);
    projectsSheet['!cols'] = [{ wch: 100 }, { wch: 100 }];

    const categoriesSheet = XLSX.utils.aoa_to_sheet(materialCategorySheet);
    categoriesSheet['!cols'] = [{ wch: 100 }];

    const uomsSheet = XLSX.utils.aoa_to_sheet(uomSheet);
    uomsSheet['!cols'] = [{ wch: 100 }];

    XLSX.utils.book_append_sheet(wb, lineItemSheet, 'Line Items');
    XLSX.utils.book_append_sheet(wb, projectsSheet, 'Valid Projects + Subsidiary');
    XLSX.utils.book_append_sheet(wb, categoriesSheet, 'Valid Material Categories');
    XLSX.utils.book_append_sheet(wb, uomsSheet, 'Valid UOMs');

    XLSX.writeFile(wb, 'RFQ_LineItems_Template.xlsx');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xxl"
      PaperProps={{ sx: { minHeight: '93vh', maxHeight: '93vh', padding: '16px 24px' } }}
    >
      <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
        <Typography variant="h6">Line Item Editor</Typography>
        <Button onClick={onClose} color="primary" variant="contained">
          Done
        </Button>
      </DialogActions>
      <DialogContent>
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
          <TextField
            type="number"
            label="Lines To Add"
            variant="outlined"
            size="small"
            value={rowsToAdd}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              setRowsToAdd(isNaN(val) ? 1 : Math.min(100, Math.max(1, val)));
            }}
            inputProps={{ min: 1, max: 100, style: { padding: '8.5px 10px', width: '80px', textAlign: 'center' } }}
          />
          <Button variant="contained" onClick={handleAddRow} size="small" disabled={isBiddingEnabled}>
            Add Line Item(s)
          </Button>
          <Button variant="contained" color="warning" size="small" onClick={handleExportTemplate}>
            Export Excel Template
          </Button>
          <Button variant="contained" color="error" size="small" component="label" disabled={isBiddingEnabled}>
            Import From Template
            <input
              type="file"
              hidden
              accept=".xlsx, .xls"
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  onImportExcel(e.target.files[0]);
                  e.target.value = ''; // Reset input so same file can be re-uploaded
                }
              }}
            />
          </Button>
        </Box>

        <Box sx={{ height: '74vh', width: '100%' }}>
          <AgGridReact
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowData={rowData}
            cellSelection={true}
            copyHeadersToClipboard={false}
            enterNavigatesVertically={true}
            enterNavigatesVerticallyAfterEdit={true}
            stopEditingWhenCellsLoseFocus={true}
            rowSelection={{
              mode: 'multiRow',
              checkboxes: false,
              headerCheckbox: false,
              suppressRowClickSelection: true,
            }}
            onCellValueChanged={(params) => {
              const field = params.colDef.field;

              if (field === 'selectedProject') {
                const match = selectedProjectSubsidiaryPairs.find((pair) => {
                  const proj = projects.find((p) => p.projectId === pair.projectId);
                  return proj?.name === params.newValue;
                });

                if (match) {
                  params.data.projectId = match.projectId;
                  params.data.netsuiteBudgetId = match.budgetId;
                  return true;
                }
                return false;
              }

              if (field === 'selectedSubsidiary') {
                const match = selectedProjectSubsidiaryPairs.find((pair) => pair.subsidiaryName === params.newValue);
                if (match) {
                  params.data.lineItemSubsidiaryId = match.subsidiaryId;
                }
              }

              if (field === 'quantity') {
                params.data.totalPrice = params.data.quantity * params.data.unitPrice;
                const selectedTax = taxCodes.find((tc) => tc.value === params.data.taxCode);
                params.data.taxRate = selectedTax?.rate ?? 0;
              }

              const updated = [];
              params.api.forEachNode((node) => {
                updated.push(node.data);
              });
              setRowData(updated);
            }}
            getContextMenuItems={(params) => {
              const cellRanges = params.api.getCellRanges() || [];

              const selectedRows = new Set();

              for (const range of cellRanges) {
                const startRowIndex = Math.min(range.startRow.rowIndex, range.endRow.rowIndex);
                const endRowIndex = Math.max(range.startRow.rowIndex, range.endRow.rowIndex);

                for (let i = startRowIndex; i <= endRowIndex; i++) {
                  const rowNode = params.api.getDisplayedRowAtIndex(i);
                  if (rowNode?.data) {
                    selectedRows.add(rowNode.data);
                  }
                }
              }

              const rowsToDelete = Array.from(selectedRows);

              return [
                'copy',
                'paste',
                'separator',
                {
                  name: `Delete ${rowsToDelete.length} Row(s)`,
                  icon: '<i class="ag-icon ag-icon-trash"></i>',
                  disabled: rowsToDelete.length === 0,
                  action: (params) => {
                    params.api.applyTransaction({ remove: rowsToDelete });

                    setRowData((prev) => {
                      const updated = prev.filter((r) => !rowsToDelete.includes(r));
                      return updated.map((item, i) => ({
                        ...item,
                        lineItemNumber: i + 1,
                      }));
                    });

                    rowsToDelete.forEach((r) => {
                      if (r.id) {
                        setDeletedRowIds((prev) => [...prev, r.id]);
                      }
                    });
                  },
                },
              ];
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RfqGridExpansionModal;